import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

/**
 * Hooks for `backend/app_core/routers/campaign_form.py`. Covers every
 * dropdown feed for the campaign form (T3.4): advertiser ids, objective
 * types, pixels, regions, age groups, ad formats, call-to-actions,
 * identities (list + inline create).
 *
 * Deviation note: the FastAPI handlers here do not declare Pydantic
 * `response_model`s, so the generated OpenAPI schema for every response
 * is `unknown`. We therefore declare explicit row shapes below, mirrored
 * from `backend/app_core/services/campaign_form.py`. When those handlers
 * grow response models, swap for the generated types.
 *
 * Pattern (per `frontend/src/api/README.md`):
 *   1) Query key starts with the router name ("campaignForm") so cache
 *      invalidation can scope by router.
 *   2) Query is gated on required params (advertiser_id, objective_type);
 *      `enabled: false` when any required field is empty so we never
 *      fire a request with a missing query string.
 *   3) `createIdentity` is a mutation that uploads multipart form data
 *      via the apiFetch FormData branch (T3.4 client extension).
 */

export interface Advertiser {
  id: string
  name: string
}

export interface ObjectiveType {
  id: string
  name: string
}

export interface Pixel {
  id: string
  name: string
}

export interface Region {
  id: string
  name: string
}

export interface AgeGroup {
  id: string
  name: string
  value?: string
  label?: string
}

export interface AdFormat {
  id: string
  name: string
}

export interface CallToActionId {
  // Backend returns a structured id wrapper; asset_ids is used as the
  // option value at render time.
  asset_ids?: string[]
  [key: string]: unknown
}

export interface CallToAction {
  id: CallToActionId
  name: string
}

export interface Identity {
  id: string
  name: string
  identity_type: string
  identity_authorized_bc_id?: string | null
}

export interface IdentitiesResponse {
  identities: Identity[]
  can_use_custom_identity: boolean
}

export interface CreateIdentityResponse {
  id: string
  name: string
  identity_type: string
  identity_authorized_bc_id?: string | null
}

export interface InstantFormPage {
  id: string
  name: string
}

export const CAMPAIGN_FORM_QUERY_KEYS = {
  all: ["campaignForm"] as const,
  advertiserIds: () => [...CAMPAIGN_FORM_QUERY_KEYS.all, "advertiser-ids"] as const,
  objectiveTypes: () => [...CAMPAIGN_FORM_QUERY_KEYS.all, "objective-types"] as const,
  pages: (advertiserId: string | null) =>
    [...CAMPAIGN_FORM_QUERY_KEYS.all, "pages", advertiserId ?? ""] as const,
  pixels: (advertiserId: string | null) =>
    [...CAMPAIGN_FORM_QUERY_KEYS.all, "pixels", advertiserId ?? ""] as const,
  regions: (advertiserId: string | null, objectiveType: string | null) =>
    [
      ...CAMPAIGN_FORM_QUERY_KEYS.all,
      "regions",
      advertiserId ?? "",
      objectiveType ?? "",
    ] as const,
  ageGroups: () => [...CAMPAIGN_FORM_QUERY_KEYS.all, "age-groups"] as const,
  interestCategories: (advertiserId: string | null) =>
    [...CAMPAIGN_FORM_QUERY_KEYS.all, "interest-categories", advertiserId ?? ""] as const,
  actionCategories: (advertiserId: string | null, scene: string) =>
    [
      ...CAMPAIGN_FORM_QUERY_KEYS.all,
      "action-categories",
      advertiserId ?? "",
      scene,
    ] as const,
  purchaseIntentionSearch: (advertiserId: string | null, keyword: string) =>
    [
      ...CAMPAIGN_FORM_QUERY_KEYS.all,
      "purchase-intention",
      advertiserId ?? "",
      keyword,
    ] as const,
  adFormats: () => [...CAMPAIGN_FORM_QUERY_KEYS.all, "ad-formats"] as const,
  callToActions: (advertiserId: string | null, objectiveType: string | null) =>
    [
      ...CAMPAIGN_FORM_QUERY_KEYS.all,
      "call-to-actions",
      advertiserId ?? "",
      objectiveType ?? "",
    ] as const,
  identities: (advertiserId: string | null) =>
    [...CAMPAIGN_FORM_QUERY_KEYS.all, "identities", advertiserId ?? ""] as const,
} as const

export function useAdvertiserIds() {
  return useQuery<Advertiser[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.advertiserIds(),
    queryFn: () =>
      apiFetch<Advertiser[]>("/api/campaign-form/advertiser-ids"),
  })
}

export function useObjectiveTypes(enabled: boolean) {
  return useQuery<ObjectiveType[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.objectiveTypes(),
    queryFn: () =>
      apiFetch<ObjectiveType[]>("/api/campaign-form/objective-types"),
    enabled,
    staleTime: Infinity,
  })
}

export function useInstantFormPages(advertiserId: string | null) {
  return useQuery<InstantFormPage[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.pages(advertiserId),
    queryFn: () =>
      apiFetch<InstantFormPage[]>(
        `/api/campaign-form/pages?advertiser_id=${encodeURIComponent(advertiserId ?? "")}`,
      ),
    enabled: Boolean(advertiserId),
  })
}

export function usePixels(advertiserId: string | null) {
  return useQuery<Pixel[]>({
    // Keyed by advertiser — switching account always fetches fresh pixels.
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.pixels(advertiserId),
    queryFn: () =>
      apiFetch<Pixel[]>(
        `/api/campaign-form/pixels?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}`,
      ),
    enabled: Boolean(advertiserId),
    // Keep a successful list for the tab lifetime. On failure: auto-retry up
    // to 3 attempts total, then show a manual Retry in the form.
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
    retry: 2, // initial try + 2 retries = 3 attempts
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  })
}

export function useRegions(
  advertiserId: string | null,
  objectiveType: string | null,
) {
  return useQuery<Region[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.regions(advertiserId, objectiveType),
    queryFn: () =>
      apiFetch<Region[]>(
        `/api/campaign-form/regions?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}&objective_type=${encodeURIComponent(objectiveType ?? "")}`,
      ),
    enabled: Boolean(advertiserId && objectiveType),
  })
}

export function useAgeGroups(enabled: boolean) {
  return useQuery<AgeGroup[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.ageGroups(),
    queryFn: () =>
      apiFetch<AgeGroup[]>("/api/campaign-form/age-groups"),
    enabled,
    staleTime: Infinity,
  })
}

export interface TargetingOption {
  id: string
  name: string
  kind?: string
  action_scene?: string
}

export interface TargetingTreeNode {
  id: string
  name: string
  kind?: string
  action_scene?: string
  children?: TargetingTreeNode[]
}

export interface TargetingCatalog {
  tree: TargetingTreeNode[]
  flat: TargetingOption[]
}

/** Catalog trees rarely change; keep them warm across tab switches. */
const TARGETING_CATALOG_STALE_MS = 30 * 60_000

export function useInterestCategories(advertiserId: string | null) {
  return useQuery<TargetingCatalog>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.interestCategories(advertiserId),
    queryFn: () =>
      apiFetch<TargetingCatalog>(
        `/api/campaign-form/interest-categories?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}`,
      ),
    enabled: Boolean(advertiserId),
    staleTime: TARGETING_CATALOG_STALE_MS,
    gcTime: TARGETING_CATALOG_STALE_MS,
  })
}

export function useActionCategories(
  advertiserId: string | null,
  actionScene: "VIDEO_RELATED" | "CREATOR_RELATED",
  enabled = true,
) {
  return useQuery<TargetingCatalog>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.actionCategories(advertiserId, actionScene),
    queryFn: () =>
      apiFetch<TargetingCatalog>(
        `/api/campaign-form/action-categories?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}&action_scene=${encodeURIComponent(actionScene)}`,
      ),
    enabled: Boolean(advertiserId) && enabled,
    staleTime: TARGETING_CATALOG_STALE_MS,
    gcTime: TARGETING_CATALOG_STALE_MS,
  })
}

export function usePurchaseIntentionSearch(
  advertiserId: string | null,
  keyword: string,
  enabled = true,
) {
  const q = keyword.trim()
  return useQuery<TargetingOption[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.purchaseIntentionSearch(advertiserId, q),
    queryFn: () =>
      apiFetch<TargetingOption[]>(
        `/api/campaign-form/purchase-intention/search?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}&keyword=${encodeURIComponent(q)}`,
      ),
    // Empty keyword loads default suggestions from the backend.
    enabled: Boolean(advertiserId) && enabled,
    staleTime: q ? 60_000 : TARGETING_CATALOG_STALE_MS,
    gcTime: TARGETING_CATALOG_STALE_MS,
  })
}

export function useAdFormats(enabled: boolean) {
  return useQuery<AdFormat[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.adFormats(),
    queryFn: () =>
      apiFetch<AdFormat[]>("/api/campaign-form/ad-formats"),
    enabled,
    staleTime: Infinity,
  })
}

export function useCallToActions(
  advertiserId: string | null,
  objectiveType: string | null,
) {
  return useQuery<CallToAction[]>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.callToActions(
      advertiserId,
      objectiveType,
    ),
    queryFn: () =>
      apiFetch<CallToAction[]>(
        `/api/campaign-form/call-to-actions?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}&objective_type=${encodeURIComponent(objectiveType ?? "")}`,
      ),
    enabled: Boolean(advertiserId && objectiveType),
  })
}

export function useIdentities(advertiserId: string | null) {
  return useQuery<IdentitiesResponse>({
    queryKey: CAMPAIGN_FORM_QUERY_KEYS.identities(advertiserId),
    queryFn: () =>
      apiFetch<IdentitiesResponse>(
        `/api/campaign-form/identities?advertiser_id=${encodeURIComponent(
          advertiserId ?? "",
        )}`,
      ),
    enabled: Boolean(advertiserId),
  })
}

export interface CreateIdentityInput {
  advertiserId: string
  displayName: string
  imageFile?: File | null
}

export function useCreateIdentity() {
  const qc = useQueryClient()
  return useMutation<CreateIdentityResponse, Error, CreateIdentityInput>({
    mutationFn: ({ advertiserId, displayName, imageFile }) => {
      const fd = new FormData()
      fd.append("advertiser_id", advertiserId)
      fd.append("display_name", displayName)
      if (imageFile) fd.append("image_file", imageFile)
      return apiFetch<CreateIdentityResponse>(
        "/api/campaign-form/identities/create",
        { method: "POST", body: fd },
      )
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: CAMPAIGN_FORM_QUERY_KEYS.identities(variables.advertiserId),
      })
    },
  })
}
